/*
ScoreControlModal — record one ad-hoc score for a control, directly from the
Control Register modal. The control analogue of ScoreRiskModal.

No CAT template, no program rollout, no checker — the score is live the moment
it is saved (gated by the Control Register add/edit permission upstream). The
score is tagged with the geography it pertains to and lands in
CTR_control_score_history, so it feeds the Register's net score / trend / signal.

Sliders (1-5) for Existence and Effectiveness, with a live Net = E × Ex (1-25)
preview. The geography picker is required; selecting several locations is only an
easier way to apply the same score to many places (one row per location is
written server-side).
*/

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Save } from 'lucide-react';
import controlRegisterService, { AdhocControlScorePayload } from '../../services/controlRegisterService';
import GeographyPicker, { GeographySelection, isGeographyComplete } from '../common/GeographyPicker';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import ScoreSlider from '../common/ScoreSlider';
import extractApiError from '../../utils/apiError';
import { useFieldValidation } from '../../validation/useFieldValidation';
import { usePermission, NOT_AUTHORISED_MSG } from '../../contexts/PermissionContext';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    controlId: number;
    controlName?: string;
    onSaved: () => void;
}

// Net control score is 0-25; thresholds mirror the unified signal cutoffs
// (Healthy ≥ 15, Warning 8-14, Breach < 8).
const NET_COLOR = (s: number) => {
    if (s >= 15) return 'text-emerald-600';
    if (s >= 8)  return 'text-amber-600';
    return 'text-red-600';
};

const ScoreControlModal: React.FC<Props> = ({ isOpen, onClose, controlId, controlName, onSaved }) => {
    const [existence, setExistence] = useState(3);
    const [effectiveness, setEffectiveness] = useState(3);
    const [dateOfTesting, setDateOfTesting] = useState<string>(() => new Date().toISOString().slice(0, 10));
    const [remarks, setRemarks] = useState('');
    const [geography, setGeography] = useState<GeographySelection>({ mode: 'locations' });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const vh = useFieldValidation();
    const { allowed: canModify, loading: permLoading } = usePermission('CTR_CONTROL_LIBRARY', 'view+modify');

    useEffect(() => {
        if (!isOpen) return;
        setExistence(3);
        setEffectiveness(3);
        setDateOfTesting(new Date().toISOString().slice(0, 10));
        setRemarks('');
        setGeography({ mode: 'locations' });
        setError(null);
        vh.reset();
    }, [isOpen, controlId]); // eslint-disable-line react-hooks/exhaustive-deps

    const net = useMemo(() => existence * effectiveness, [existence, effectiveness]);
    const deficiency = useMemo(() => 25 - net, [net]);
    const geoReady = useMemo(() => isGeographyComplete(geography), [geography]);

    if (!isOpen) return null;

    const handleSave = async () => {
        if (!canModify) { setError(NOT_AUTHORISED_MSG); return; }
        if (!geoReady) { setError('Select at least one geography for this score.'); return; }
        // Format guard on the two free-input fields (both optional / present-only).
        const okFields = vh.validateAll([
            { name: 'remarks', code: 110, value: remarks },
            { name: 'date_of_testing', code: 305, value: dateOfTesting },
        ]);
        if (!okFields) return;
        setSubmitting(true);
        setError(null);
        try {
            const payload: AdhocControlScorePayload = {
                effectiveness_score: effectiveness,
                existence_score: existence,
                date_of_testing: dateOfTesting || undefined,
                remarks: remarks || undefined,
                geography,
            };
            await controlRegisterService.recordAdhocScore(controlId, payload);
            onSaved();
            onClose();
        } catch (e) {
            setError(extractApiError(e, 'Failed to record score'));
        } finally {
            setSubmitting(false);
        }
    };

    return createPortal(
        // z-9999 matches the app's standard Modal overlay so the GeographyPicker's
        // location dropdown (portalled to <body> at z-index 9999) renders ABOVE
        // this modal instead of behind it. Do not raise to 10000 — that re-hides
        // the picker panel.
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
                <div className="p-5 border-b border-slate-100 flex justify-between items-center">
                    <div>
                        <h2 className="text-lg font-bold text-slate-800">Record Control Score</h2>
                        {controlName && <p className="text-xs text-slate-500 mt-0.5">{controlName}</p>}
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="BTN_control_score_close"><X size={22} /></button>
                </div>

                {error && (
                    <div className="m-4 mb-0 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm" data-testid="MSG_control_score_error">{error}</div>
                )}

                <div className="p-5 space-y-5 overflow-y-auto">
                    <div className="border-2 border-gray-400 rounded-lg shadow-sm bg-white">
                        <div className="grid grid-cols-2 gap-6 p-4">
                            <div className="space-y-4">
                                <ScoreSlider label="Existence"     value={existence}     onCommit={setExistence}     widthClass="w-full" min={0} />
                                <ScoreSlider label="Effectiveness" value={effectiveness} onCommit={setEffectiveness} widthClass="w-full" min={0} />
                            </div>
                            <div className="space-y-2 flex flex-col justify-center">
                                <div className="flex justify-between items-center">
                                    <span className="text-xs text-slate-500 uppercase">Net (Existence × Effectiveness)</span>
                                    <span className={`text-2xl font-black ${NET_COLOR(net)}`} data-testid="VAL_control_score_net">{net} <span className="text-sm font-semibold text-slate-400">/ 25</span></span>
                                </div>
                                <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                                    <span className="text-xs text-slate-500 uppercase">Net Deficiency</span>
                                    <span className="text-sm font-bold text-red-600">{deficiency} / 25</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Geography<span className="text-red-500 ml-0.5" aria-hidden="true">*</span></label>
                        <GeographyPicker value={geography} onChange={setGeography} data-testid="GEO_control_score" />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <FormControl.DatePicker
                                label="Date of Testing"
                                value={dateOfTesting}
                                onChange={(e) => { setDateOfTesting(e.target.value); vh.clearError('date_of_testing'); }}
                                onBlur={() => vh.validateField('date_of_testing', 305, dateOfTesting)}
                                error={vh.errors['date_of_testing']}
                                data-testid="INP_control_score_date"
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1">Remarks</label>
                        <textarea
                            value={remarks}
                            onChange={(e) => { setRemarks(e.target.value); vh.clearError('remarks'); }}
                            onBlur={(e) => vh.validateField('remarks', 110, e.target.value)}
                            placeholder="What was assessed? Why this score?"
                            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm h-20"
                            data-testid="INP_control_score_remarks"
                        />
                        {vh.errors['remarks'] && (
                            <div className="response-inline-warning">{vh.errors['remarks']}</div>
                        )}
                    </div>
                </div>

                <div className="p-4 border-t border-slate-100 flex justify-end gap-3">
                    <Button
                        variant="fill"
                        icon={Save}
                        onClick={handleSave}
                        disabled={permLoading || !canModify || submitting || !geoReady}
                        title={!canModify ? NOT_AUTHORISED_MSG : undefined}
                        data-testid="BTN_control_score_submit"
                    >
                        Submit
                    </Button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default ScoreControlModal;
